import {
  CalendarClock,
  Camera,
  Hourglass,
  MapPin,
  Phone,
  PhoneMissed,
  Trash2,
  Wrench,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import {
  CALL_SLOTS,
  LEAD_TEMPERATURES,
  daysSince,
  formatDate,
  labelOf,
} from "./gulaConstants";
import { AgentChip } from "./AgentChip";
import {
  durationLabel,
  entryLabel,
  isUncontacted,
  minutesSince,
  waitTone,
} from "./leadAge";
import { isTrashable } from "./leadTrash";
import { RequirementsBadge } from "./RequirementsBadge";
import type { ContactNotes } from "./notes/clientNotes";
import { NotePreview } from "./notes/NotePreview";
import { clockLabel, leadClock } from "./pipelineHygiene";
import { CALLING_STAGES as TAKEABLE_STAGES } from "./shifts";
import { fullName, type GulaClient } from "./gulaTypes";

/** "hace 2 h", "hace 3 d"… for the last call attempt. */
const timeAgo = (date: string) => {
  const minutes = Math.max(
    0,
    Math.round((Date.now() - new Date(date).getTime()) / 60000),
  );
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  return `hace ${Math.round(hours / 24)} d`;
};

/** Stages where the requirements have not been asked yet or no longer matter. */
const NO_REQUIREMENTS_STAGES = ["new", "no_answer", "no_purchase"];

/** Stages where the agent is still trying to reach the lead. */
const CALLING_STAGES = ["new", "no_answer"];

// Left edge of the card: how likely the lead is to buy
const TEMPERATURE_EDGE: Record<string, string> = {
  hot: "border-l-red-500",
  warm: "border-l-amber-400",
  cold: "border-l-sky-400",
};

const isOverdue = (date?: string | null) =>
  !!date && new Date(date).getTime() < new Date().setHours(0, 0, 0, 0);

const WAIT_STYLE = {
  ok: "text-emerald-700 dark:text-emerald-300",
  late: "text-amber-700 dark:text-amber-400",
  very_late: "text-destructive",
};

/** "Entró hoy 10:42 · 35 min sin contactar" */
const EntryLine = ({ client, now }: { client: GulaClient; now: number }) => {
  const waiting = isUncontacted(client)
    ? minutesSince(client.created_at, now)
    : null;
  return (
    <span className="text-xs text-muted-foreground">
      Entró {entryLabel(client.created_at, now)}
      {waiting != null ? (
        <span className={`font-semibold ${WAIT_STYLE[waitTone(waiting)]}`}>
          {" "}
          · {durationLabel(waiting)} sin contactar
        </span>
      ) : null}
    </span>
  );
};

export const ClientCard = ({
  client,
  onOpen,
  agentName,
  onTake,
  onNoAnswer,
  dayOwnerName,
  meId,
  notes,
  releasedFromName,
  canDecide,
  onDecide,
  onBackToNew,
  onTrash,
  now,
}: {
  client: GulaClient;
  onOpen: (client: GulaClient) => void;
  /** Name of the agent working the lead; empty when nobody has taken it */
  agentName?: string;
  /** Takes the lead for the signed-in agent */
  onTake?: (client: GulaClient) => void;
  /** Records a call nobody picked up */
  onNoAnswer?: (client: GulaClient) => void;
  /** Whose list for today has this lead ("ti" for me) */
  dayOwnerName?: string;
  meId?: unknown;
  /** Team notes: the latest one shows on the card */
  notes?: ContactNotes;
  /** First name of whoever had the lead before it was freed */
  releasedFromName?: string;
  /** The signed-in user owns the lead or is an admin */
  canDecide?: boolean;
  /** Opens the keep / release / reactivate choice of a lead in limbo */
  onDecide?: (client: GulaClient) => void;
  /** "Reactivar" column: puts the lead back in "Nuevo" */
  onBackToNew?: (client: GulaClient) => void;
  /** Admins: sends a lead that is not a real lead to the trash */
  onTrash?: (client: GulaClient) => void;
  /** Current time, to show how long the lead has been waiting */
  now?: number;
}) => {
  const canTake =
    !!onTake &&
    !dayOwnerName &&
    TAKEABLE_STAGES.includes(client.stage) &&
    (client.sales_id == null || String(client.sales_id) === String(meId));
  const days = daysSince(client.stage_changed_at);
  const clock = leadClock(client);
  const isFreed = client.sales_id == null && client.released_from != null;
  const temperature = LEAD_TEMPERATURES.find(
    (option) => option.value === client.lead_temperature,
  );
  return (
    <Card
      className={`cursor-pointer gap-0 border-l-4 py-2.5 transition-shadow hover:shadow-md ${
        TEMPERATURE_EDGE[client.lead_temperature ?? ""] ?? "border-l-border"
      }`}
      onClick={() => onOpen(client)}
      title={client.campaign_name ?? undefined}
    >
      <CardContent className="flex flex-col gap-1.5 px-3">
        <div className="flex items-start justify-between gap-2">
          <span className="font-medium leading-tight">{fullName(client)}</span>
          <div className="flex shrink-0 items-center gap-1">
            <span
              className="whitespace-nowrap text-xs text-muted-foreground"
              title="Días en esta fase"
            >
              {days === 0 ? "hoy" : `${days} d`}
            </span>
            {client.sales_id != null && agentName ? (
              <AgentChip id={client.sales_id} name={agentName} compact />
            ) : null}
            {onTrash && isTrashable(client) ? (
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6 text-muted-foreground hover:text-destructive"
                title="A la basura: no es un lead (compañero, instalador…)"
                aria-label="Mandar a la basura"
                onClick={(event) => {
                  event.stopPropagation();
                  onTrash(client);
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            ) : null}
          </div>
        </div>
        {now ? <EntryLine client={client} now={now} /> : null}
        {client.phone ? (
          <a
            href={`tel:${client.phone}`}
            onClick={(event) => event.stopPropagation()}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:underline"
          >
            <Phone className="h-3 w-3" />
            {client.phone}
          </a>
        ) : null}
        <div className="flex min-w-0 flex-wrap gap-1 [&>*]:max-w-full [&>*]:truncate">
          {isFreed && client.stage !== "reactivation" ? (
            <Badge
              className="border-transparent bg-lime-200 text-neutral-900"
              title={client.release_reason ?? undefined}
            >
              Libre{releasedFromName ? ` · era de ${releasedFromName}` : ""}
            </Badge>
          ) : null}
          {temperature ? (
            <Badge className={`border-transparent ${temperature.className}`}>
              {temperature.label}
            </Badge>
          ) : null}
          {client.zone ? (
            <Badge
              variant={
                client.zone === "Sin cobertura" ? "destructive" : "secondary"
              }
              className="gap-1"
            >
              <MapPin className="h-3 w-3" />
              {client.zone}
            </Badge>
          ) : null}
          {client.call_slot ? (
            <Badge
              variant="outline"
              className="whitespace-normal"
              title={labelOf(CALL_SLOTS, client.call_slot)}
            >
              {client.call_slot.split(",").join(" · ")} h
            </Badge>
          ) : null}
          {client.maintenance_status === "active" ||
          client.maintenance_status === "trialing" ? (
            <Badge className="gap-1 border-transparent bg-emerald-100 text-emerald-800 dark:bg-emerald-400/15 dark:text-emerald-200">
              <Wrench className="h-3 w-3" />
              Mantenimiento
              {client.maintenance_interval === "year" ? " anual" : " mensual"}
            </Badge>
          ) : client.maintenance_status === "past_due" ||
            client.maintenance_status === "unpaid" ? (
            <Badge variant="destructive" className="gap-1">
              <Wrench className="h-3 w-3" />
              Impago mantenimiento
            </Badge>
          ) : null}
          {client.rental_status === "active" ||
          client.rental_status === "trialing" ? (
            <Badge className="border-transparent bg-sky-100 text-sky-800 dark:bg-sky-400/15 dark:text-sky-200">
              Alquiler
            </Badge>
          ) : client.rental_status === "past_due" ||
            client.rental_status === "unpaid" ? (
            <Badge variant="destructive">Impago alquiler</Badge>
          ) : null}
          {!NO_REQUIREMENTS_STAGES.includes(client.stage) &&
          client.order_stage !== "installed" ? (
            <RequirementsBadge record={client} />
          ) : null}
          {client.nb_photo_notes ? (
            <Badge variant="outline" className="gap-1">
              <Camera className="h-3 w-3" />
              {client.nb_photo_notes}
            </Badge>
          ) : null}
        </div>
        {notes?.team.length ? (
          <NotePreview notes={notes} lines={2} onClick={() => onOpen(client)} />
        ) : null}
        {client.stage === "recontact" && client.recontact_at ? (
          <span
            className={`flex items-center gap-1 text-xs ${
              isOverdue(client.recontact_at)
                ? "font-semibold text-destructive"
                : "text-muted-foreground"
            }`}
          >
            <CalendarClock className="h-3 w-3" />
            Recontactar {formatDate(client.recontact_at)}
          </span>
        ) : null}
        {client.call_attempts ? (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <PhoneMissed className="h-3 w-3" />
            {client.call_attempts}{" "}
            {client.call_attempts === 1 ? "intento" : "intentos"}
            {client.last_call_attempt_at
              ? ` · último ${timeAgo(client.last_call_attempt_at)}`
              : ""}
          </span>
        ) : null}
        {client.stage === "no_answer" && (client.call_attempts ?? 0) >= 2 ? (
          <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
            Prueba a llamarle en otra franja
          </span>
        ) : null}
        {client.stage === "reactivation" && client.release_reason ? (
          <span className="text-xs text-muted-foreground">
            {releasedFromName ? `Era de ${releasedFromName} · ` : ""}
            {client.release_reason}
          </span>
        ) : null}
        {clock ? (
          <span
            className={`flex items-center gap-1 text-xs font-medium ${
              clock.kind === "release"
                ? "text-amber-700 dark:text-amber-400"
                : "text-destructive"
            }`}
            title={
              clock.kind === "release"
                ? "Una llamada, una nota o un WhatsApp y vuelve a contar desde cero"
                : undefined
            }
          >
            <Hourglass className="h-3 w-3" />
            {clockLabel(clock)}
          </span>
        ) : null}
        {dayOwnerName ? (
          <span className="w-fit rounded-full bg-lime-200 px-2 py-0.5 text-xs font-medium text-neutral-900">
            En la jornada de {dayOwnerName}
          </span>
        ) : null}
        <div className="mt-1 flex flex-wrap gap-1.5 [&>*]:flex-1">
          {canTake ? (
            <Button
              size="sm"
              variant="outline"
              className="h-7"
              title="Pasa a tu lista de hoy en Mi jornada"
              onClick={(event) => {
                event.stopPropagation();
                onTake?.(client);
              }}
            >
              {client.sales_id == null ? "Coger lead" : "A mi jornada"}
            </Button>
          ) : null}
          {clock?.kind === "limbo" && canDecide && onDecide ? (
            <Button
              size="sm"
              variant="outline"
              className="h-7"
              title="Seguir con él, soltarlo o pasarlo a Reactivar"
              onClick={(event) => {
                event.stopPropagation();
                onDecide(client);
              }}
            >
              Decidir
            </Button>
          ) : null}
          {client.stage === "reactivation" && onBackToNew ? (
            <Button
              size="sm"
              variant="outline"
              className="h-7"
              title="Vuelve a la cadena, libre para quien lo coja"
              onClick={(event) => {
                event.stopPropagation();
                onBackToNew(client);
              }}
            >
              Vuelve a Nuevo
            </Button>
          ) : null}
          {CALLING_STAGES.includes(client.stage) && onNoAnswer ? (
            <Button
              size="sm"
              variant="outline"
              className="h-7"
              title="Le he llamado y no lo ha cogido"
              onClick={(event) => {
                event.stopPropagation();
                onNoAnswer(client);
              }}
            >
              <PhoneMissed className="h-3.5 w-3.5" />
              No contesta
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};
